import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import * as UIcons from '@iconscout/react-unicons'
import Spinner from 'components/Spinner'
import { showModal } from 'store/slices/miscSlice'
import useCurrentUser from 'hooks/useCurrentUser'
import PageHeader from '../shared/PageHeader'
import DetailList from '../shared/DetailList'
import ChangePasswordForm from './components/ChangePasswordForm'
import DeleteAccountConfirm from './components/DeleteAccountConfirm'
import EditProfile from './components/EditProfile'
import Logout from 'includes/Layout/NavBar/Logout'
import { AccountSettingsValidation } from 'pages/Settings/validation'

export default withTranslation()(function AccountSettings({ t }) {
  const dispatch = useDispatch()
  const { user, isLoading } = useCurrentUser()
  const [showLogout, setShowLogout] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showEditProfile, setShowEditProfile] = useState(false)

  const profileItems = [
    { icon: UIcons.UilEnvelope, label: t('settings.account_security.my_account.fields.email'), value: user?.email },
    { icon: UIcons.UilPhone, label: t('settings.account_security.my_account.fields.phone'), value: user?.phone },
    { icon: UIcons.UilBriefcaseAlt, label: t('settings.account_security.my_account.fields.job_title'), value: user?.job_position },
    { icon: UIcons.UilLanguage, label: t('settings.account_security.my_account.fields.language'), value: user?.preferred_language },
    { icon: UIcons.UilClock, label: t('settings.account_security.my_account.fields.timezone'), value: user?.timezone }
  ]

  const openChangePassword = () => dispatch(showModal({
    title: t('settings.account_settings.change_pwd'),
    method: 'post',
    content: ChangePasswordForm,
    validation: AccountSettingsValidation,
    url: 'auth/change-password',
    successMsg: 'Password reset successful',
    formClass: 'w-11/12 md:w-[390px]'
  }))

  const passwordItems = [
    { icon: UIcons.UilLock, label: t('settings.account_settings.current_pwd.label'), value: '••••••••••••' },
    { icon: UIcons.UilLock, label: t('settings.account_settings.new_pwd.label'), value: '••••••••••••' },
    { icon: UIcons.UilLock, label: t('settings.account_settings.confirm_pwd.label'), value: '••••••••••••' }
  ]

  return (
    <section className="w-full max-w-2xl mx-auto">
      <PageHeader
        crumbs={[
          { name: t('settings.hub.title'), url: '/settings' },
          { name: t('settings.account_security.title') }
        ]}
        title={t('settings.account_security.title')}
      />

      <div className="bg-background rounded-2xl overflow-hidden mb-5">
        <div className="flex items-center justify-between gap-3 px-4 py-2">
          <span className="text-base font-semibold text-secondary uppercase">
            {t('settings.account_security.my_account.title')}
          </span>

          <button
            type="button"
            onClick={() => setShowEditProfile(true)}
            className="flex items-center gap-2 bg-white border border-border-lines rounded-full px-4 py-2 text-sm font-semibold text-dark-blue"
          >
            <UIcons.UilPen size={16} /> {t('settings.account_security.edit_profile.button')}
          </button>
        </div>

        <div className="px-2 pb-2">
          <div className="bg-white rounded-lg p-4">
            {
              isLoading ? <Spinner /> : (
                <>
                  <div className="flex items-center gap-6 p-4 mb-2">
                    <span className="inline-flex rounded-full overflow-hidden bg-primary w-24 h-24 shrink-0 border border-border-lines">
                      {
                        user?.avatar ? (
                          <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl font-bold text-white m-auto">
                            {`${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`}
                          </span>
                        )
                      }
                    </span>

                    <div>
                      <h2 className="text-2xl font-semibold text-dark-blue">{[user?.first_name, user?.last_name].filter(Boolean).join(' ')}</h2>
                      <p className="text-sm text-secondary">{user?.email}</p>
                    </div>
                  </div>

                  <DetailList items={profileItems} />
                </>
              )
            }
          </div>
        </div>
      </div>

      <div className="bg-background rounded-2xl overflow-hidden mb-5">
        <div className="flex items-center justify-between gap-3 px-4 py-2">
          <span className="text-base font-semibold text-secondary uppercase">
            {t('settings.account_settings.change_pwd')}
          </span>

          <button
            type="button"
            onClick={openChangePassword}
            className="flex items-center gap-2 bg-white border border-border-lines rounded-full px-4 py-2 text-sm font-semibold text-dark-blue"
          >
            <UIcons.UilPen size={16} /> {t('settings.account_settings.change_pwd')}
          </button>
        </div>

        <div className="px-2 pb-2">
          <div className="bg-white rounded-lg p-4">
            <DetailList items={passwordItems} />
          </div>
        </div>
      </div>

      <div className="bg-background rounded-2xl p-2 flex flex-col gap-2">
        <button
          type="button"
          className="w-full bg-white rounded-lg flex items-center justify-between px-4 py-4 hover:bg-background text-left"
          onClick={() => setShowLogout(true)}
        >
          <span className="flex items-center gap-4 text-base text-dark-blue">
            <UIcons.UilSignout size={24} className="text-secondary" /> {t('settings.account_security.logout')}
          </span>

          <UIcons.UilAngleRight className="text-secondary" size={24} />
        </button>

        <button
          type="button"
          className="w-full bg-white rounded-lg flex items-center justify-between px-4 py-4 hover:bg-background text-left"
          onClick={() => setShowDelete(true)}
        >
          <span className="flex items-center gap-4 text-base text-dark-blue">
            <UIcons.UilTrash size={24} className="text-secondary" /> {t('settings.account_security.delete_account.title')}
          </span>

          <UIcons.UilAngleRight className="text-secondary" size={24} />
        </button>
      </div>

      {showLogout && <Logout handleClick={() => setShowLogout(false)} />}
      {showDelete && <DeleteAccountConfirm handleClick={() => setShowDelete(false)} />}
      {showEditProfile && <EditProfile user={user} handleClick={() => setShowEditProfile(false)} />}
    </section>
  )
})
